/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getEnabledRollUps,
    formatProductAnalyticsReportDates,
    formatProductAnalyticsTableDate,
    chartTableColors,
    chartZAxisColor
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './commentingusersreport_styles';



/* ========== ~~~~~~~~~~ COMMENTING ANALYTICS - COMMENTING USERS REPORT ~~~~~~~~~~ ========== */
const CommentingUsersReport = injectSheet(styles)((props) => {

    // props
    const { selectedInterval, setSelectedInterval } = props;

    // redux state
    const { overview, details, status } = useSelector((state) => state.commenting.commentingUsers);
    const { filters } = useSelector((state) => state.app);
    const changeType = filters.compareTo;

    // component states
    const [chartLegends, setChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    // component constants
    const products = [
        { id: 'uniqueCommentingUsers', name: 'Unique Commenting Users', color: chartTableColors[0] },
        { id: 'avgTimeOnSiteUptick', name: 'Avg. Time on Site Uptick', color: chartZAxisColor }
    ];
    const dates = details && status === 'fulfilled' ? details[selectedInterval] : {};

    // show aggregate total on load of page
    useEffect(() => {
        if (status === 'fulfilled' && overview) showAggregateTotal();
    }, [overview]);

    // show aggregate total on chart mouse leave
    useEffect(() => {
        if (showTotal) showAggregateTotal();
    }, [showTotal]);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if (status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details, 'commenting');
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
            setChartLegends([]);
        }
    }, [status]);

    const convertNumToHoursMins = num => {
        const mins = Math.floor(num / 60);
        const secs = parseInt(num % 60);
        let formattedTime = '';

        if(mins > 60) {
            const hours = Math.floor(mins / 60);
            const min = parseInt(mins % 60);
            const formattedHours = hours > 0 ? `${hours}${min > 0 ? ':' : ':00 hr'}` : '';
            const unit = hours > 0 ? 'hr' : 'min';
            const minDigits = min < 10 ? `0${min}` : min;
            const formattedMins = min > 0 ? `${minDigits} ${unit}` : '';
            formattedTime = `${formattedHours}${formattedMins}`;
        } else {
            const formattedMins = mins > 0 ? `${mins}${secs > 0 ? ':' : ':00 min'}` : '';
            const unit = mins > 0 ? 'min' : 'sec';
            const secDigits = secs < 10 ? `0${secs}` : secs;
            const formattedSecs = secs > 0 ? `${secDigits} ${unit}` : '';
            formattedTime = `${formattedMins}${formattedSecs}`;
        }

        return formattedTime;
    }

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
            changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        setChartLegends(productArr);
    };

    const getBarChartKeys = () => products.map(product => product.name).reverse();

    const getBarChartColors = () => products.map(product => product.color).reverse();

    const renderBarChartLegends = () => {
        return chartLegends.map((legend, index) =>
        (
            <ChartLegend
                title={legend.name}
                checkboxColor={legend.color}
                checked={legend.checked}
                onCheck={checked => {
                    const legends = [...chartLegends];
                    legends[index] = {
                        ...legends[index],
                        checked: checked
                    }

                    setChartLegends(legends);
                }}
                value={legend.id === 'avgTimeOnSiteUptick' ? convertNumToHoursMins(legend.value) : formatNumber(legend.value)}
                changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                changeType={changeType}
                metricType={'commenting'}
            />
        )
        )
    }

    const transformBarChartData = () => {
        let barChartData = [];
        barChartData = Object.keys(dates).map((date, index) => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = { date: formattedDate }
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(product => product.checked === true);
                if (checkedProducts.map(product => product.id).indexOf(product.id) >= 0) {
                    if (product.id === 'avgTimeOnSiteUptick')
                        item['z'] = detailsDateData[product.id];
                    else {
                        item[product.name] = detailsDateData[product.id];
                        item[`${product.name}Color`] = product.color;
                    }
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
            if (productDetails) {
                value = productDetails[product.id] ? productDetails[product.id] : 0;
                changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
    }

    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedPeriodX('');
    }


    const monetizationBarChart = {
        indexBy: 'date',
        data: transformBarChartData(),
        keys: getBarChartKeys(),
        colors: getBarChartColors(),
        legends: renderBarChartLegends()
    }

    const cardtoggles = (
        <CardToggles>
            <CardToggle
                label={'Unique Commenting Users'}
                compareType={changeType}
                metricType={'commenting'}
                percentChange={{
                    value: overview && overview.period ? formatNumber(overview.period.uniqueCommentingUsersRateChange, 'percent') : 0,
                    direction: overview && overview.period ? getChangeRateDirection(overview.period.uniqueCommentingUsersRateChange) : 'up'
                }}
                selected={true}
                value={overview?.period ? formatNumber(overview.period.uniqueCommentingUsers) : ''}
            />
        </CardToggles>
    );

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={monetizationBarChart.colors}
            keys={monetizationBarChart.keys}
            data={monetizationBarChart.data}
            period={selectedInterval}
            loading={status === 'pending' ? true : false}
            onHover={handleHoverOnBarChart}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            zType={'time'}
            zAxisId={'avgTimeOnSiteUptick'}
            zAxisColor={chartZAxisColor}
        />
    );

  const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'commenting'}
            products={products}
            data={details ? details[selectedInterval] : null}
            csvFileName={'Unique Commenting Users Report'}
        />
    ) : []

    const isNoData = () => {
        return (status === 'rejected') ||
            (status === 'fulfilled' && !overview?.period?.uniqueCommentingUsers) ||
            (status === 'fulfilled' && Object.keys(overview).length === 0)
    }

    return (
        <div name={'commentingusersreport'} class={`${props.classes.commentingusersreport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={'Unique Commenting Users'}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>Unique Commenting Users</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>
                                Unique commenting users are defined as a user making at least one comment for the selected rolled-up time period.
                                To calculate the Avg Time on Site Uptick metric we estimate how much additional time commenting users spend on the
                                site for the rolled-up time period. The estimate is based on data from both known and unknown users. The estimated
                                reaction time is allocated as follows: Comment/Reply - 60 seconds, Reaction (vote/emoji) - 5 seconds, Load more
                                comments - 90 seconds, and Show more replies - 60 seconds. Once we have the total time, we divide the above number
                                by taking the user groups of commenters and users who click load more comments/replies.
                            </p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={barchart}
                // loading={status === 'pending' ? true : false}
                noData={isNoData()}
                enabledPeriods={enabledRollups}
                chartLegend={monetizationBarChart.legends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default CommentingUsersReport;
